/* global __CLIENT__, FM */ //eslint-disable-line
/* eslint-disable no-unused-expressions */
import React, { useState, useEffect } from 'react';
import { matchRoutes, renderRoutes } from 'react-router-config';
import { useLocation, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import * as queryString from 'query-string';
import { StatsigProvider } from '@statsig/react-bindings';
import PropTypes from 'prop-types';

import '../../assets/global.scss';
import '../../assets/android-banner.scss';
import Header from './components/Header';
import Footer from './components/Footer';
import Polling from './components/Polling';
import PopupContainer from './components/PopupContainer';
import withTracker from './components/hocs/withTracker';
import { useStatsig } from './components/hooks/useStatsig';
import ProgressBar from './components/ProgressBar';
import { getUserAndTypes } from './actions/combined-actions';
import { addFreshchatRestoreId } from './api';
import Notifications from './components/Notifications';
import LoginPopUpWrapper from './components/PopUps/LoginPopUpWrapper';
import { closeLoginPopUp } from './actions/login-popup-actions';
import ErrorBoundary from './components/ErrorBoundary';

import MenuNew from './components/Menu';
import { getApiAxiosInstance } from './utils/network';
import ImpersonateMemberNotification from './components/ImpersonateMember/Notification';
import { setAuthHeader } from './actions';
import { getFeedPreferences } from './actions/settings-actions';
import { shouldDisplayActivateUtrNotification } from './utils/activateUtr';
import { getDefaultCustomFields } from './utils/statsigUserFields';

import config from '../config';

const Environments = {
    PROD: 'prod',
    DEVELOPMENT: 'dev',
};

function AppContent(props) {
    const [showHeader, setShowHeader] = useState(true);
    const { checkGate, featureGates } = useStatsig();

    const header = useSelector((state) => state.header);
    const loginPopUp = useSelector((state) => state.loginPopUp);
    const user = useSelector((state) => state.auth?.user);
    const feedPreferences = useSelector((state) => state.auth?.feedPreferences);
    const isFeedPreferencesLoading = useSelector((state) => state.auth?.isFeedPreferencesLoading);
    const activateUtr = useSelector((state) => state.activateUtr);

    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();

    const getRoute = (location) => {
        const matchedRoutes = matchRoutes(props.route.routes, location.pathname);
        return matchedRoutes[0]?.route;
    };

    const destroyFreshChatWidget = () => {
        if (window?.fcWidget !== undefined) {
            window.fcWidget.destroy();
        }
    };

    const initFreshchatWidget = async (user) => {
        const isFreshchatEnabled = checkGate(featureGates.FRESHCHAT_ENABLED);

        if (isFreshchatEnabled && window?.fcWidget) {
            const widgetOptions = {
                token: config.freshChatToken,
                host: 'https://wchat.freshchat.com',
                config: { showFAQOnOpen: false },
                externalId: user?.id,
                restoreId: user?.freshchatRestoreId || null,
            };

            window.fcWidget.init(widgetOptions);

            const setUserProperties = () => {
                if (user && window.fcWidget.user) {
                    window.fcWidget.user.setProperties({
                        firstName: user.firstName,
                        lastName: user.lastName,
                        email: user.email,
                        subscriptionType: user.subscriptionType,
                    });
                }
            };

            try {
                const response = await window.fcWidget.user?.get();
                if (response?.status === 200) {
                    setUserProperties();
                }
            } catch (error) {
                setUserProperties();
                window.fcWidget.on('user:created', (createdResponse) => {
                    if (createdResponse?.status === 200) {
                        const restoreId = createdResponse.data?.restoreId;
                        if (restoreId) {
                            const axiosInstance = getApiAxiosInstance();
                            addFreshchatRestoreId(axiosInstance, restoreId);
                        }
                    }
                });
            }
        }
    };

    useEffect(() => {
        const parsed = queryString.parse(window?.location?.search);
        const { jwt } = parsed;
        if (jwt) {
            setAuthHeader({ jwt });
        }

        // Clean up URL hash after cookie consent modal closes
        const listenerName = 'hashchange';
        const handleHashChange = () => {
            // When the hash is empty (just "#" with nothing after), clean up the URL
            if (window.location.hash === '' || window.location.hash === '#') {
                window.history.replaceState(null, document.title, window.location.pathname + window.location.search);
            }
        };

        window.addEventListener(listenerName, handleHashChange);

        return () => {
            window.removeEventListener(listenerName, handleHashChange);
        };
    }, []);

    useEffect(() => {
        const currentRoute = getRoute(location);
        const showWidget = currentRoute?.supportWidget ? !currentRoute.supportWidget.disabled : true;
        const headerDisabled = !!currentRoute?.header?.disabled;
        const env = (process.env.CONFIG_SOURCE || 'development').toLowerCase();
        const isFreshChatWidgetInitialized = window?.fcWidget?.isInitialized();
        const shouldFetchFeedPreferences = !feedPreferences && !isFeedPreferencesLoading;
        const environment = (env === 'production' || env === 'prod') ? Environments.PROD : Environments.DEVELOPMENT;
        const hasDatadogInProduction = (env === 'production' || env === 'prod') && window?.datadogRum;

        const createFreshChatWidget = async () => {
            await initFreshchatWidget(user);
        };

        if (user) {
            if (showWidget && !isFreshChatWidgetInitialized) {
                createFreshChatWidget();
            } else if (!showWidget) {
                destroyFreshChatWidget();
            }
            if (shouldFetchFeedPreferences) {
                dispatch(getFeedPreferences());
            }
            if (window.dataLayer) {
                window.dataLayer.push({
                    event: 'user_identified',
                    user_id: user.id,
                    env: environment,
                });
            }

            if (hasDatadogInProduction) {
                window.datadogRum.setUser({
                    id: user.id,
                    // add other attributes as needed
                });
            }
        } else {
            if (window?.dataLayer) {
                window.dataLayer.push({
                    event: 'user_identified',
                    user_id: null,
                    env: environment,
                });
            }
            if (hasDatadogInProduction) {
                window.datadogRum.setUser({});
            }
            destroyFreshChatWidget();
        }
        setShowHeader(!headerDisabled);
    }, [user, feedPreferences, location?.pathname]);

    if (__CLIENT__ && user && window?.FM && typeof window?.FM?.identify === 'function') {
        FM.identify(user?.email);
    }

    const isEventCreation = location.pathname.includes('/events/create');
    const isEventEditPage = location.pathname.includes('/events') && location.pathname.includes('/edit');
    const currentRoute = getRoute(location);
    const { mobileWebView } = currentRoute;
    const showExpandedNav = !!user || user?.onboardingCompleted || !user?.onboardingRequired;
    const isImpersonating = user?.impersonationOriginatingMemberId && (user?.impersonationOriginatingMemberId !== user?.id);
    const activatingUtr = shouldDisplayActivateUtrNotification(activateUtr, location.pathname);
    const env = (process.env.CONFIG_SOURCE || 'development').toLowerCase();

    const closeLoginModal = () => {
        loginPopUp.onCloseClick?.();
        dispatch(closeLoginPopUp());
    };

    return (
        <div
            id="myutr-app-wrapper"
            className={`theme-v2 
                ${showExpandedNav ? `expanded-nav ${header.type === 'title' ? 'title' : ''}` : ''}
                ${!user ? 'logged-out' : ''}
                ${!showHeader || header.disabled ? 'no-header' : ''}
                ${header.custom ? 'custom-header' : ''}
                ${header.style === 'transparent' ? 'transparent' : ''}
                ${activatingUtr ? 'activating-utr' : ''}
                ${isImpersonating ? 'impersonating' : ''}
                ${mobileWebView ? 'web-view' : ''}
                ${env}
            `}
        >
            {isImpersonating && (
                <ImpersonateMemberNotification
                    user={user}
                    history={history}
                />
            )}
            <ProgressBar />
            <Header
                isImpersonating={isImpersonating}
                themeV2={true}
            />
            <MenuNew />
            <ErrorBoundary main>
                <div id="myutr-app-body">
                    {renderRoutes(props.route.routes)}
                </div>
            </ErrorBoundary>
            {!isEventCreation && !isEventEditPage && (currentRoute.footer ? !currentRoute.footer.disabled : true) && <Footer />}
            <Polling />
            <Notifications currentRoute={currentRoute} />
            {loginPopUp.showPopUp && (
                <LoginPopUpWrapper
                    {...props}
                    onCloseClick={closeLoginModal}
                    userParamsNeeded={loginPopUp.userParamsNeeded}
                    performAction={loginPopUp.performAction}
                    extraFunctionCalls={loginPopUp.extraFunctionCalls}
                    onboardFlow={loginPopUp.onboardFlow}
                    type={loginPopUp.type}
                    titles={loginPopUp.titles}
                    screen={loginPopUp?.screen}
                    showCloseIcon={loginPopUp?.showCloseIcon}
                    closeOnClickOutside={true}
                />
            )}
            <PopupContainer />
        </div>
    );
}

AppContent.propTypes = {
    route: PropTypes.shape({
        routes: PropTypes.arrayOf(PropTypes.any).isRequired,
    }).isRequired,
};

function App(props) {
    const user = useSelector((state) => state.auth?.user);
    const env = (process.env.CONFIG_SOURCE || 'development').toLowerCase();
    const isWindowUndefined = typeof window === 'undefined';
    const environmentTier = (env === 'production' || env === 'prod') ? env : 'development';

    return (
        <StatsigProvider
            sdkKey={config.statsigKey}
            user={{
                userID: user?.id || null,
                email: user?.email || null,
                custom: getDefaultCustomFields(user),
            }}
            options={{
                networkConfig: {
                    preventAllNetworkTraffic: isWindowUndefined,
                },
                disableStorage: isWindowUndefined,
                environment: { tier: environmentTier },
            }}
            loadingComponent={<div />}
        >
            <AppContent {...props} />
        </StatsigProvider>
    );
}

App.propTypes = {
    route: PropTypes.shape({
        routes: PropTypes.arrayOf(PropTypes.any).isRequired,
    }).isRequired,
};

export default {
    component: withTracker(App),
    loadData: ({ dispatch }) => dispatch(getUserAndTypes()),
};
